import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Clock,
  CheckCircle2,
  XCircle,
  MapPin,
  FileText,
  Calendar,
  User,
  ArrowRight,
  Camera,
  Star,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { cacheGet, cacheSet } from "../../lib/cache";
import BackButton from "../../components/BackButton";
import { Spinner, Alert, formatRupiah } from "../../components/ui";
import RatingForm from "../../components/RatingForm";
import { StarRating } from "../../components/StarRating";
import { useAuth } from "../../context/AuthContext";
import PaymentStatus from "../../components/PaymentStatus";

const STATUS_CONFIG = {
  PENDING_WORKER: { label: "Menunggu Pekerja", color: "bg-amber-50 text-amber-600", icon: Clock },
  ACCEPTED: { label: "Diterima", color: "bg-blue-50 text-blue-600", icon: CheckCircle2 },
  ON_PROGRESS: { label: "Berlangsung", color: "bg-brand-50 text-brand-600", icon: Clock },
  COMPLETED: { label: "Selesai", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
  CONFIRMED: { label: "Dikonfirmasi", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
  REJECTED: { label: "Ditolak", color: "bg-red-50 text-red-600", icon: XCircle },
  CANCELLED: { label: "Dibatalkan", color: "bg-gray-50 text-gray-600", icon: XCircle },
  DISPUTED: { label: "Disputa", color: "bg-red-50 text-red-600", icon: XCircle },
};

const TIMELINE_STEPS = [
  { key: "PENDING_WORKER", label: "Menunggu Pekerja" },
  { key: "ACCEPTED", label: "Diterima Pekerja" },
  { key: "ON_PROGRESS", label: "Pekerjaan Berlangsung" },
  { key: "COMPLETED", label: "Pekerjaan Selesai" },
  { key: "CONFIRMED", label: "Dikonfirmasi" },
];

export default function CustomerBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(() => cacheGet(`booking:${id}`).data);
  const [loading, setLoading] = useState(() => booking === null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [beforeUrl, setBeforeUrl] = useState(null);
  const [proofUrl, setProofUrl] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const { data } = await api.get(`/api/bookings/${id}`);
        setBooking(data);
        cacheSet(`booking:${id}`, data, 30000);
        cacheSet(`booking:${id}`, data, 30000);
        setError(null);
      } catch (err) {
        if (!cacheGet(`booking:${id}`).data) setError("Pesanan tidak ditemukan.");
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id]);

  const refreshBooking = async () => {
    try {
      const { data } = await api.get(`/api/bookings/${id}`);
      setBooking(data);
      cacheSet(`booking:${id}`, data, 30000);
    } catch {}
  };

  useEffect(() => {
    if (booking?.has_before_proof) {
      api.get(`/api/bookings/${id}/proof?type=before`, { responseType: "blob" })
        .then((r) => setBeforeUrl(URL.createObjectURL(r.data)))
        .catch(() => {});
    }
  }, [booking?.has_before_proof, id]);

  useEffect(() => {
    if (booking?.has_proof) {
      api.get(`/api/bookings/${id}/proof?type=after`, { responseType: "blob" })
        .then((r) => setProofUrl(URL.createObjectURL(r.data)))
        .catch(() => {});
    }
  }, [booking?.has_proof, id]);

  useEffect(() => {
    if (booking?.status === "CONFIRMED") {
      api.get(`/api/reviews/booking/${id}`)
        .then((r) => {
          setReviews(r.data || []);
          cacheSet(`booking-reviews:${id}`, r.data || [], 60000);
        })
        .catch(() => {});
    } else {
      const cached = cacheGet(`booking-reviews:${id}`).data;
      if (cached) setReviews(cached);
    }
  }, [booking?.status, id]);

  useEffect(() => {
    if (["ACCEPTED", "ON_PROGRESS", "COMPLETED", "CONFIRMED"].includes(booking?.status)) {
      api.get(`/api/change-orders/booking/${id}`)
        .then((r) => {
          setChangeOrders(r.data || []);
          cacheSet(`booking-co:${id}`, r.data || [], 30000);
        })
        .catch(() => {});
    } else {
      const cached = cacheGet(`booking-co:${id}`).data;
      if (cached) setChangeOrders(cached);
    }
  }, [booking?.status, id]);

  // Countdown for auto-confirm; refetch when window elapses
  useEffect(() => {
    if (booking?.status !== "COMPLETED" || !booking.auto_confirm_at) return;
    const t = setInterval(() => {
      const target = new Date(booking.auto_confirm_at).getTime();
      const next = Date.now();
      setNowTick(next);
      if (next >= target) {
        api.get(`/api/bookings/${id}`)
          .then((r) => setBooking(r.data))
          .catch(() => {});
      }
    }, 1000);
    return () => clearInterval(t);
  }, [booking?.status, booking?.auto_confirm_at, id]);

  const handleConfirm = async () => {
    setActionLoading(true);
    try {
      await api.post(`/api/bookings/${id}/confirm`);
      const { data } = await api.get(`/api/bookings/${id}`);
      setBooking(data);
      cacheSet(`booking:${id}`, data, 30000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  const cancelPct = (() => {
    if (!booking?.scheduled_at) return 100;
    const hours = (new Date(booking.scheduled_at).getTime() - Date.now()) / 3600000;
    if (hours > 24) return 100;
    if (hours >= 12) return 50;
    return 0;
  })();

  const handleCancel = async () => {
    if (!window.confirm(`Yakin ingin membatalkan? Dana kembali ${cancelPct}% sesuai aturan pembatalan.`)) return;
    setActionLoading(true);
    try {
      await api.post(`/api/bookings/${id}/cancel`);
      const { data } = await api.get(`/api/bookings/${id}`);
      setBooking(data);
      cacheSet(`booking:${id}`, data, 30000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCOAction = async (coId, action, note = "") => {
    setActionLoading(true);
    setError(null);
    try {
      await api.post(`/api/change-orders/${coId}/${action}`, { note });
      const { data } = await api.get(`/api/change-orders/booking/${id}`);
      setChangeOrders(data || []);
      const { data: updatedBooking } = await api.get(`/api/bookings/${id}`);
      setBooking(updatedBooking);
      cacheSet(`booking:${id}`, updatedBooking, 30000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeReason.trim()) return;
    setDisputeLoading(true);
    setError(null);
    try {
      await api.post(`/api/bookings/${id}/dispute`, { reason: disputeReason });
      const { data } = await api.get(`/api/bookings/${id}`);
      setBooking(data);
      cacheSet(`booking:${id}`, data, 30000);
      setShowDisputeForm(false);
      setDisputeReason("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setDisputeLoading(false);
    }
  };

  if (loading) return <Spinner />;
  if (error || !booking) {
    return (
      <div className="animate-fade-up grid place-items-center py-8">
        <div className="container space-y-6">
          <BackButton to="/customer/bookings" />
          <div className="card p-10 text-center">
            <p className="font-bold text-brand-800">{error || "Pesanan tidak ditemukan"}</p>
          </div>
        </div>
      </div>
    );
  }

  const status = STATUS_CONFIG[booking.status] || STATUS_CONFIG.PENDING_WORKER;
  const StatusIcon = status.icon;

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const timelineIndex = TIMELINE_STEPS.findIndex((s) => s.key === booking.status);
  const isTerminal = ["REJECTED", "CANCELLED", "DISPUTED"].includes(booking.status);

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container space-y-6">
        <BackButton to="/customer/bookings" />

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-brand-800">Detail Pesanan</h1>
            <p className="text-brand-400 mt-1 text-sm">{booking.id?.slice(0, 8)}</p>
          </div>
          <span className={`chip ${status.color}`}>
            <StatusIcon className="h-4 w-4" /> {status.label}
          </span>
        </div>

        {error && <Alert kind="error">{error}</Alert>}

        {/* Timeline */}
        {!isTerminal && (
          <div className="card p-6">
            <h2 className="text-brand-800 text-lg mb-4">Status Pesanan</h2>
            <div className="space-y-3">
              {TIMELINE_STEPS.map((step, idx) => {
                const isPast = idx < timelineIndex;
                const isCurrent = idx === timelineIndex;
                return (
                  <div key={step.key} className="flex items-center gap-3">
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isPast
                          ? "bg-success-500 text-white"
                          : isCurrent
                          ? "bg-brand-600 text-white"
                          : "bg-brand-100 text-brand-400"
                      }`}
                    >
                      {isPast ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <span className="text-sm font-bold">{idx + 1}</span>
                      )}
                    </div>
                    <span
                      className={`font-medium ${
                        isCurrent ? "text-brand-800" : isPast ? "text-success-600" : "text-brand-400"
                      }`}
                    >
                      {step.label}
                    </span>
                    {isCurrent && (
                      <span className="chip bg-brand-50 text-brand-600 text-xs ml-auto">Saat ini</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="card p-6 space-y-4">
          <h2 className="text-brand-800 text-lg">Informasi Pesanan</h2>

          <div className="flex items-center gap-3 text-brand-600">
            <FileText className="h-5 w-5 flex-shrink-0" />
            <div>
              <p className="text-sm text-brand-400">Layanan</p>
              <p className="font-medium">{booking.service?.name || "-"}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-brand-600">
            <User className="h-5 w-5 flex-shrink-0" />
            <div>
              <p className="text-sm text-brand-400">Pekerja</p>
              <p className="font-medium">{booking.worker?.display_name || "-"} ({booking.worker?.tier || "LITE"})</p>
            </div>
          </div>

          {booking.scheduled_at && (
            <div className="flex items-center gap-3 text-brand-600">
              <Calendar className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="text-sm text-brand-400">Jadwal</p>
                <p className="font-medium">{formatDate(booking.scheduled_at)}</p>
              </div>
            </div>
          )}

          {booking.location && (
            <div className="flex items-center gap-3 text-brand-600">
              <MapPin className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="text-sm text-brand-400">Lokasi</p>
                <p className="font-medium">{booking.location}</p>
              </div>
            </div>
          )}

          {booking.customer_note && (
            <div className="flex items-center gap-3 text-brand-600">
              <FileText className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="text-sm text-brand-400">Catatan</p>
                <p className="font-medium">{booking.customer_note}</p>
              </div>
            </div>
          )}

          <div className="border-t border-brand-50 pt-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-brand-400">Estimasi Biaya</p>
              <p className="text-2xl font-extrabold text-brand-800">{formatRupiah(booking.quoted_amount)}</p>
            </div>
            {booking.final_amount && (
              <div className="text-right">
                <p className="text-sm text-brand-400">Biaya Akhir</p>
                <p className="text-2xl font-extrabold text-success-600">{formatRupiah(booking.final_amount)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Payment Status */}
        <PaymentStatus bookingId={id} bookingStatus={booking.status} role="customer" />

        {/* Completion Proof */}
        {booking.has_proof && (
          <div className="card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Camera className="h-5 w-5 text-success-600" />
              <h2 className="text-brand-800 text-lg">Bukti Sebelum & Sesudah</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-brand-500 mb-2">Sebelum</p>
                {beforeUrl ? (
                  <div className="rounded-xl overflow-hidden border border-brand-100">
                    <img src={beforeUrl} alt="Sebelum pengerjaan" className="w-full max-h-48 object-contain" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-4 bg-brand-50 rounded-xl text-brand-400 text-sm">
                    <Camera className="h-6 w-6 mr-1" /> Memuat...
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-brand-500 mb-2">Sesudah</p>
                {proofUrl ? (
                  <div className="rounded-xl overflow-hidden border border-brand-100">
                    <img src={proofUrl} alt="Sesudah pengerjaan" className="w-full max-h-48 object-contain" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-4 bg-brand-50 rounded-xl text-brand-400 text-sm">
                    <Camera className="h-6 w-6 mr-1" /> Memuat...
                  </div>
                )}
              </div>
            </div>
            {booking.completion_note && (
              <div className="mt-3 p-3 bg-brand-50 rounded-xl">
                <p className="text-sm text-brand-400">Catatan pekerja:</p>
                <p className="text-brand-700 mt-1">{booking.completion_note}</p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {booking.status === "COMPLETED" && (
          <div className="card p-6 border-success-200 bg-success-50" data-testid="auto-confirm-card">
            <p className="font-bold text-success-700 mb-1">Pekerjaan Selesai</p>
            <p className="text-sm text-success-600 mb-2">
              Pastikan pekerjaan sudah sesuai. Jika ada yang kurang, ajukan komplain dulu.
            </p>
            {booking.auto_confirm_at && (
              <p className="text-xs text-brand-500 mb-4" data-testid="auto-confirm-countdown">
                Konfirmasi otomatis dalam{" "}
                <span className="font-bold text-brand-700">
                  {(() => {
                    const ms = Math.max(0, new Date(booking.auto_confirm_at).getTime() - nowTick);
                    const h = Math.floor(ms / 3600000);
                    const m = Math.floor((ms % 3600000) / 60000);
                    const s = Math.floor((ms % 60000) / 1000);
                    return `${h}j ${m}m ${s}d`;
                  })()}
                </span>{" "}
                jika tidak ada respons (72 jam sejak selesai).
              </p>
            )}
            {!showDisputeForm ? (
              <div className="flex gap-3">
                <button
                  onClick={handleConfirm}
                  className="btn-success flex-1"
                  disabled={actionLoading}
                  data-testid="confirm-booking"
                >
                  {actionLoading ? "Memproses..." : <span className="flex items-center gap-2 justify-center"><CheckCircle2 className="h-4 w-4" /> Konfirmasi Selesai</span>}
                </button>
                <button
                  onClick={() => setShowDisputeForm(true)}
                  className="btn-ghost flex-1 border-amber-300 text-amber-700"
                  disabled={actionLoading}
                  data-testid="dispute-button-completed"
                >
                  <AlertTriangle className="h-4 w-4" /> Ajukan Komplain
                </button>
              </div>
            ) : (
              <div className="space-y-3 p-4 bg-white rounded-xl border border-amber-200">
                <div>
                  <label className="text-sm text-brand-500 mb-1 block">Alasan Komplain</label>
                  <textarea
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    placeholder="Jelaskan masalah yang terjadi (contoh: masih ada bagian yang belum dikerjakan, hasil tidak sesuai)... "
                    rows={3}
                    className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none resize-none"
                    data-testid="dispute-reason"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowDisputeForm(false); setDisputeReason(""); }}
                    className="btn-ghost flex-1 !py-2"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleDispute}
                    className="btn flex-1 !py-2 bg-amber-500 text-white hover:bg-amber-600"
                    disabled={disputeLoading || !disputeReason.trim()}
                    data-testid="submit-dispute"
                  >
                    {disputeLoading ? "Mengirim..." : "Kirim ke CS"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {["PENDING_WORKER", "ACCEPTED"].includes(booking.status) && (
          <div className="card p-6">
            <p className="font-bold text-brand-800 mb-1">Batalkan Pesanan?</p>
            <p className="text-xs text-brand-500 mb-3" data-testid="cancel-refund-info">
              Aturan refund: &gt;24 jam sebelum jadwal kembali 100%, 12-24 jam kembali 50%, &lt;12 jam tidak ada refund.
              Pembatalan ini: <span className="font-bold text-brand-700">kembali {cancelPct}%</span>
              {booking.scheduled_at ? "" : " (tanpa jadwal: penuh)"}.
            </p>
            <button
              onClick={handleCancel}
              className="btn-ghost border-red-200 text-red-600 w-full"
              disabled={actionLoading}
              data-testid="cancel-booking"
            >
              {actionLoading ? "Memproses..." : "Batalkan Pesanan"}
            </button>
          </div>
        )}

        {/* Change Orders */}
        {changeOrders.length > 0 && (
          <div className="card p-6">
            <h2 className="text-brand-800 text-lg mb-3">Perubahan Harga/Scope</h2>
            <div className="space-y-3">
              {changeOrders.map((co) => (
                <div key={co.id} className={`p-4 rounded-xl border ${
                  co.status === "PENDING" ? "border-amber-200 bg-amber-50" :
                  co.status === "ACCEPTED" ? "border-success-200 bg-success-50" :
                  "border-red-200 bg-red-50"
                }`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-brand-800 text-sm">{co.description}</p>
                      <p className={`text-sm font-bold mt-1 ${
                        co.additional_amount >= 0 ? "text-brand-700" : "text-success-600"
                      }`}>
                        {co.additional_amount >= 0 ? "+" : ""}{formatRupiah(co.additional_amount)}
                      </p>
                    </div>
                    <span className={`chip text-xs ${
                      co.status === "PENDING" ? "bg-amber-100 text-amber-700" :
                      co.status === "ACCEPTED" ? "bg-success-100 text-success-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {co.status === "PENDING" ? "Menunggu" : co.status === "ACCEPTED" ? "Diterima" : "Ditolak"}
                    </span>
                  </div>
                  {co.customer_note && (
                    <p className="text-xs text-brand-500 mt-2">Catatan: {co.customer_note}</p>
                  )}
                  <p className="text-xs text-brand-400 mt-1">
                    {new Date(co.proposed_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}
                  </p>

                  {/* Customer actions for pending change orders */}
                  {co.status === "PENDING" && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleCOAction(co.id, "reject", "Ditolak oleh pelanggan")}
                        className="btn-ghost flex-1 !py-2 border-red-200 text-red-600 text-sm"
                        disabled={actionLoading}
                        data-testid={`reject-co-${co.id}`}
                      >
                        <XCircle className="h-4 w-4" /> Tolak
                      </button>
                      <button
                        onClick={() => handleCOAction(co.id, "accept", "Disetujui oleh pelanggan")}
                        className="btn-success flex-1 !py-2 text-sm"
                        disabled={actionLoading}
                        data-testid={`accept-co-${co.id}`}
                      >
                        <CheckCircle2 className="h-4 w-4" /> Setujui
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dispute / Appeal - for ACCEPTED and ON_PROGRESS */}
        {["ACCEPTED", "ON_PROGRESS"].includes(booking.status) && booking.status !== "DISPUTED" && (
          <div className="card p-6 border-amber-200 bg-amber-50">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-brand-800 text-lg">Ajukan Komplain</h2>
              {!showDisputeForm && (
                <button
                  onClick={() => setShowDisputeForm(true)}
                  className="btn-ghost !py-2 !px-3 text-sm border-amber-300 text-amber-700"
                  data-testid="dispute-button"
                >
                  Komplain ke CS
                </button>
              )}
            </div>
            <p className="text-sm text-amber-600 mb-3">
              Jika pekerja tidak hadir atau ada masalah, ajukan komplain ke customer service.
            </p>

            {showDisputeForm && (
              <div className="space-y-3 p-4 bg-white rounded-xl border border-amber-200">
                <div>
                  <label className="text-sm text-brand-500 mb-1 block">Alasan Komplain</label>
                  <textarea
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    placeholder="Jelaskan masalah yang terjadi (contoh: pekerja tidak datang sesuai jadwal)..."
                    rows={3}
                    className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none resize-none"
                    data-testid="dispute-reason"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowDisputeForm(false); setDisputeReason(""); }}
                    className="btn-ghost flex-1 !py-2"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleDispute}
                    className="btn flex-1 !py-2 bg-amber-500 text-white hover:bg-amber-600"
                    disabled={disputeLoading || !disputeReason.trim()}
                    data-testid="submit-dispute"
                  >
                    {disputeLoading ? "Mengirim..." : "Kirim ke CS"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {booking.status === "DISPUTED" && (
          <div className="card p-6 border-red-200 bg-red-50">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-bold text-red-700">Komplain Diajukan</p>
                <p className="text-sm text-red-600">Menunggu review dari Customer Service.</p>
                {booking.dispute_reason && (
                  <p className="text-sm text-red-500 mt-1">Alasan: {booking.dispute_reason}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reviews */}
        {booking.status === "CONFIRMED" && (
          <>
            {/* Reviews dari pekerja untuk pelanggan ini */}
            {reviews.filter((r) => r.review_type === "CUSTOMER" && r.reviewer_id !== user?.id).length > 0 && (
              <div className="space-y-3">
                <h2 className="text-brand-800">Review dari Pekerja</h2>
                {reviews
                  .filter((r) => r.review_type === "CUSTOMER" && r.reviewer_id !== user?.id)
                  .map((r) => (
                    <div key={r.id} className="card p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-bold text-brand-800 text-sm">{r.reviewer_name || "Pekerja"}</p>
                        <StarRating rating={r.rating} />
                      </div>
                      {r.comment && <p className="text-sm text-brand-600">{r.comment}</p>}
                      <p className="text-xs text-brand-400 mt-2">{new Date(r.created_at).toLocaleDateString("id-ID")}</p>
                    </div>
                  ))}
              </div>
            )}

            {/* Form untuk pelanggan me-review pekerja */}
            {!reviews.some((r) => r.reviewer_id === user?.id && r.review_type === "WORKER") && (
              <RatingForm
                bookingId={id}
                reviewType="WORKER"
                onReviewed={() => {
                  api.get(`/api/reviews/booking/${id}`).then((r) => setReviews(r.data || []));
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
